import { Button, alpha } from '@mui/material';
import { siopColors } from '../../config/theme';

/**
 * The app's button. Every pressable control should be this rather than a raw
 * MUI `<Button>`, so height, radius, casing and colour are decided once.
 *
 * Three things it grew to earn that role:
 *
 *   `variant` — a dialog needs Save AND Cancel, and two filled pills side by
 *   side say they are equally weighted when they are not. 'outlined' and 'text'
 *   render the same shape in the same colour at lower weight, so the whole
 *   dialog can still be one component.
 *
 *   `Icon={null}` — the default `+` is right for "Add…" and wrong for Save,
 *   Retry or Cancel. Passing null drops it. (`iconOnly` is unchanged: a round
 *   32px button carrying just the glyph.)
 *
 *   `component` / `href` — a control that leaves the app must be a real anchor
 *   or middle-click and "open in new tab" stop working. `component="a"` keeps
 *   the styling and restores both.
 *
 * `color` still overrides per instance, but the default is the theme's primary
 * (siopColors.primary) rather than a hardcoded value — it used to be a teal
 * that appeared nowhere else in the palette.
 */
const FilledButton = ({
  btnname,
  onClick,
  // NO icon by default. It used to default to a `+`, which is right for
  // "Add…" and wrong for Save, Retry, Close and most of the app — so the
  // majority of call sites had to pass Icon={null} to turn it off. Buttons
  // that want a glyph now say so.
  Icon = null,
  endIcon,
  color = siopColors.primary,
  variant = 'contained',
  type = 'button',
  // The standard width every button starts at, so a row of them lines up
  // instead of each shrink-wrapping its own label.
  //
  // Applied as a FLOOR, not a clamp: `textWrap: nowrap` means a hard width
  // clips anything longer, and the app has labels ("Open Sync Debug Panel",
  // "Back to Uploads Workspace") that do not fit 140px. Short labels — Save,
  // Cancel, Post, Close — all land on exactly this width, which is where the
  // uniformity actually shows. A percentage width is passed through as-is, for
  // the full-width cases.
  width = '140px',
  disabled = false,
  fontSize,
  transition = false,
  borderRadius = '20px',
  iconOnly = false,
  component,
  href,
  target,
  rel,
  sx,
  ...rest
}) => {
  const filled = variant === 'contained';

  return (
    <Button
      variant={variant}
      component={component}
      href={href}
      target={target}
      rel={rel}
      data-ignoreblur="true"
      sx={{
        // A contained button carries the colour; the other two wear it as ink
        // and let the surface behind them show through, which is what makes a
        // Cancel beside a Save read as the lighter of the two.
        bgcolor: filled ? color : 'transparent',
        color: filled ? '#fff' : color,
        borderColor: variant === 'outlined' ? alpha(color, 0.5) : undefined,
        borderRadius: iconOnly ? '50%' : borderRadius,
        textTransform: 'none',
        fontWeight: 600,
        // Hover used to repeat the base colour exactly, so a filled button gave
        // no feedback at all. Each variant now moves, in its own register.
        '&:hover': {
          bgcolor: filled ? color : alpha(color, 0.08),
          filter: filled ? 'brightness(0.92)' : 'none',
          borderColor: variant === 'outlined' ? color : undefined,
        },
        fontSize: fontSize || '0.82rem',
        height: '32px',
        textWrap: 'nowrap',
        minWidth: iconOnly ? '32px' : width,
        width: iconOnly ? '32px' : String(width).includes('%') ? width : 'auto',
        // Stops the button stretching to the full width of a column flex
        // container. `width: auto` on a flex item means "stretch on the cross
        // axis", so any button dropped into a stacked layout — the TTA
        // hand-off under the chart, a dialog action column — came out as a
        // full-width bar. `max-content` sizes it to its label; `minWidth`
        // still wins where the two conflict, so short labels keep the standard
        // 140px. Percentage widths opt out, since those ask to fill on purpose.
        maxWidth: iconOnly || String(width).includes('%') ? undefined : 'max-content',
        px: iconOnly ? 0 : 1.5,
        transition: transition ? '0.3s' : 'none',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        ...sx,
      }}
      startIcon={iconOnly ? null : Icon}
      endIcon={iconOnly ? null : endIcon}
      onClick={onClick}
      type={type}
      disabled={disabled}
      {...rest}
    >
      {!iconOnly && btnname}
      {iconOnly && Icon}
    </Button>
  );
};

export default FilledButton;
